import React, { useState } from 'react';
import { Send, Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { internationalBanks } from '../../data/internationalBanks';
import { userAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const WireTransfer = ({ 
  profile, 
  fetchProfile, 
  onOTPRequired,
  showReceipt,
  receiptData,
  setShowReceipt,
  setActiveTab
}) => {
  const { toast } = useToast();
  const [wireTransferType, setWireTransferType] = useState('domestic');
  const [useManualBankEntry, setUseManualBankEntry] = useState(false);
  const [showIntermediaryBank, setShowIntermediaryBank] = useState(false);
  const [routingValidation, setRoutingValidation] = useState({ valid: null, bankName: '', message: '' });
  const [transferFee, setTransferFee] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  
  const [transferData, setTransferData] = useState({
    toAccountNumber: '',
    routingNumber: '',
    bankName: '',
    recipientName: '',
    recipientAddress: '',
    bankAddress: '',
    amount: '',
    swiftCode: '',
    iban: '',
    intermediaryBankName: '',
    intermediarySwiftCode: '',
    intermediaryBankAddress: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const validateRoutingNumber = (routing) => {
    if (!routing || routing.length !== 9) {
      setRoutingValidation({ valid: false, bankName: '', message: 'Routing number must be 9 digits' });
      return false;
    }

    const digits = routing.split('').map(Number);
    const checksum = (3 * (digits[0] + digits[3] + digits[6]) +
                      7 * (digits[1] + digits[4] + digits[7]) +
                      (digits[2] + digits[5] + digits[8])) % 10;

    if (checksum !== 0) {
      setRoutingValidation({ valid: false, bankName: '', message: 'Invalid routing number format' });
      return false;
    }

    const bankDatabase = {
      '021000021': 'JPMorgan Chase Bank',
      '026009593': 'Bank of America',
      '111000025': 'Wells Fargo Bank',
      '121000248': 'Wells Fargo Bank (California)',
      '322271627': 'Chase Bank',
      '011401533': 'TD Bank',
      '031201360': 'Citibank',
      '044000037': 'PNC Bank',
      '063100277': 'U.S. Bank',
      '122105278': 'Morgan Stanley Private Bank'
    };

    const bankName = bankDatabase[routing] || 'Valid US Bank';
    setRoutingValidation({ valid: true, bankName, message: `✓ ${bankName} verified` });
    return true;
  };

  const calculateTransferFee = (amount, type) => {
    const amt = parseFloat(amount) || 0;
    let fee = 0;
    
    if (type === 'domestic') {
      fee = amt * 0.001;
      fee = Math.max(5, Math.min(25, fee));
    } else if (type === 'international') {
      fee = amt * 0.015;
      fee = Math.max(25, Math.min(75, fee));
    }
    
    setTransferFee(fee);
    setTotalAmount(amt + fee);
    return fee;
  };

  const handleRoutingChange = (value) => {
    setTransferData({...transferData, routingNumber: value});
    if (value.length === 9) {
      validateRoutingNumber(value);
    } else {
      setRoutingValidation({ valid: null, bankName: '', message: '' });
    }
  };

  const handleAmountChange = (value, type = 'domestic') => {
    setTransferData({...transferData, amount: value});
    calculateTransferFee(value, type);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    onOTPRequired(transferData);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  if (showReceipt && receiptData) {
    return (
      <Card className="print:shadow-none">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-800 text-white print:bg-white print:text-black">
          <CardTitle className="text-center text-2xl">Wire Transfer Receipt</CardTitle>
          <p className="text-center text-sm opacity-90 print:text-gray-600">Morgan Guaranty Trust</p>
        </CardHeader>
        <CardContent className="p-8">
          <div className="mb-6 text-center">
            <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full mb-4 print:bg-white print:border print:border-green-600">
              <span className="text-lg font-bold">✓ Transfer Successful</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 border-t border-b py-4">
            <div>
              <p className="text-sm text-gray-600">Reference Number</p>
              <p className="font-bold text-lg">{receiptData.reference}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Date & Time</p>
              <p className="font-medium">{receiptData.date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Transfer Type</p>
              <p className="font-medium capitalize">{receiptData.transferType}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Amount</p>
              <p className="font-bold text-xl text-blue-600">${parseFloat(receiptData.amount).toLocaleString('en-US', {minimumFractionDigits: 2})}</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border">
              <h3 className="font-bold mb-3 text-gray-900">From Account</h3>
              <p className="text-sm"><span className="font-medium">Account:</span> {receiptData.accountNumber}</p>
              <p className="text-sm"><span className="font-medium">New Balance:</span> ${parseFloat(receiptData.newBalance).toLocaleString('en-US', {minimumFractionDigits: 2})}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg print:bg-white print:border">
              <h3 className="font-bold mb-3 text-gray-900">Recipient Details</h3>
              <p className="text-sm"><span className="font-medium">Name:</span> {receiptData.recipientName}</p>
              <p className="text-sm"><span className="font-medium">Bank:</span> {receiptData.bankName}</p>
              {receiptData.transferType === 'international' ? (
                <>
                  <p className="text-sm"><span className="font-medium">SWIFT:</span> {receiptData.swiftCode}</p>
                  <p className="text-sm"><span className="font-medium">IBAN:</span> {receiptData.iban}</p>
                </>
              ) : (
                <>
                  <p className="text-sm"><span className="font-medium">Account:</span> {receiptData.toAccountNumber}</p>
                  <p className="text-sm"><span className="font-medium">Routing:</span> {receiptData.routingNumber}</p>
                </>
              )}
              <p className="text-sm"><span className="font-medium">Address:</span> {receiptData.recipientAddress}</p>
            </div>
          </div>

          <div className="flex gap-4 print:hidden">
            <Button onClick={handlePrintReceipt} className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </Button>
            <Button onClick={() => {setShowReceipt(false); setActiveTab('overview');}} variant="outline" className="flex-1">
              Back to Dashboard
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-6">
            This is a simulated transaction for demonstration purposes only.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Wire Transfer</CardTitle>
        <p className="text-sm text-gray-600">Transfer funds domestically or internationally</p>
      </CardHeader>
      <CardContent>
        <div className="flex space-x-4 border-b mb-6">
          <button
            onClick={() => setWireTransferType('domestic')}
            className={`px-6 py-3 font-medium ${wireTransferType === 'domestic' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            Domestic (US Banks)
          </button>
          <button
            onClick={() => setWireTransferType('international')}
            className={`px-6 py-3 font-medium ${wireTransferType === 'international' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            International
          </button>
        </div>

        <form onSubmit={handleTransfer} className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Available Balance:</strong> ${profile.balance.toLocaleString('en-US', {minimumFractionDigits: 2})}
            </p>
          </div>

          {wireTransferType === 'domestic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Account Number *
                </label>
                <Input
                  type="text"
                  value={transferData.toAccountNumber}
                  onChange={(e) => setTransferData({...transferData, toAccountNumber: e.target.value})}
                  placeholder="Enter account number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Routing Number *
                </label>
                <Input
                  type="text"
                  maxLength={9}
                  value={transferData.routingNumber}
                  onChange={(e) => handleRoutingChange(e.target.value.replace(/\D/g, ''))}
                  placeholder="9-digit routing number"
                  required
                  className={routingValidation.valid === false ? 'border-red-500' : routingValidation.valid === true ? 'border-green-500' : ''}
                />
                {routingValidation.message && (
                  <p className={`text-xs mt-1 ${routingValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                    {routingValidation.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name *
                </label>
                <Input
                  type="text"
                  value={routingValidation.bankName || transferData.bankName}
                  onChange={(e) => setTransferData({...transferData, bankName: e.target.value})}
                  placeholder="Auto-filled from routing number"
                  required
                  readOnly={routingValidation.bankName !== ''}
                  className={routingValidation.bankName ? 'bg-green-50' : ''}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Name *
                </label>
                <Input
                  type="text"
                  value={transferData.recipientName}
                  onChange={(e) => setTransferData({...transferData, recipientName: e.target.value})}
                  placeholder="Enter recipient name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Address *
                </label>
                <Textarea
                  value={transferData.recipientAddress}
                  onChange={(e) => setTransferData({...transferData, recipientAddress: e.target.value})}
                  placeholder="Enter recipient address"
                  required
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Address *
                </label>
                <Textarea
                  value={transferData.bankAddress}
                  onChange={(e) => setTransferData({...transferData, bankAddress: e.target.value})}
                  placeholder="Enter bank address"
                  required
                  rows={3}
                />
              </div>

              <div className="md:col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Available Balance:</span>
                    <span className="text-lg font-bold text-blue-600">{formatCurrency(profile.balance)}</span>
                  </div>
                  {transferFee > 0 && (
                    <>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Transfer Amount:</span>
                        <span>{formatCurrency(parseFloat(transferData.amount) || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Transfer Fee (0.1%):</span>
                        <span>{formatCurrency(transferFee)}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-blue-300 mt-2 pt-2">
                        <span className="font-semibold text-gray-900">Total Amount:</span>
                        <span className="text-lg font-bold text-blue-700">{formatCurrency(totalAmount)}</span>
                      </div>
                      {totalAmount > profile.balance && (
                        <p className="text-xs text-red-600 mt-2">⚠ Insufficient funds</p>
                      )}
                    </>
                  )}
                </div>
                
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Amount *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={transferData.amount}
                    onChange={(e) => handleAmountChange(e.target.value, 'domestic')}
                    placeholder="0.00"
                    required
                    className="pl-8 text-lg font-semibold"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Domestic wire: 0.1% fee (min $5, max $25)
                </p>
              </div>
            </div>
          )}

          {wireTransferType === 'international' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Bank or Enter Manually
                </label>
                <div className="flex items-center space-x-4 mb-3">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <Checkbox 
                      checked={!useManualBankEntry}
                      onCheckedChange={(checked) => {
                        setUseManualBankEntry(!checked);
                        if (checked) {
                          setTransferData({...transferData, bankName: '', swiftCode: ''});
                        }
                      }}
                    />
                    <span className="text-sm">Select from list</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <Checkbox 
                      checked={useManualBankEntry}
                      onCheckedChange={setUseManualBankEntry}
                    />
                    <span className="text-sm">Enter manually</span>
                  </label>
                </div>
                
                {!useManualBankEntry ? (
                  <Select 
                    value={transferData.bankName} 
                    onValueChange={(value) => {
                      const selectedBank = internationalBanks.find(b => b.name === value);
                      setTransferData({
                        ...transferData, 
                        bankName: value,
                        swiftCode: selectedBank?.code || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select international bank" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {internationalBanks.map((bank, index) => (
                        <SelectItem key={index} value={bank.name}>
                          {bank.country} - {bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type="text"
                    value={transferData.bankName}
                    onChange={(e) => setTransferData({...transferData, bankName: e.target.value})}
                    placeholder="Enter institution name"
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SWIFT Code *
                </label>
                <Input
                  type="text"
                  value={transferData.swiftCode}
                  onChange={(e) => setTransferData({...transferData, swiftCode: e.target.value})}
                  placeholder="Auto-filled or enter manually"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IBAN / Account Number *
                </label>
                <Input
                  type="text"
                  value={transferData.iban || transferData.toAccountNumber}
                  onChange={(e) => setTransferData({...transferData, iban: e.target.value, toAccountNumber: e.target.value})}
                  placeholder="Enter IBAN or account number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Name *
                </label>
                <Input
                  type="text"
                  value={transferData.recipientName}
                  onChange={(e) => setTransferData({...transferData, recipientName: e.target.value})}
                  placeholder="Enter recipient name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Address *
                </label>
                <Textarea
                  value={transferData.recipientAddress}
                  onChange={(e) => setTransferData({...transferData, recipientAddress: e.target.value})}
                  placeholder="Enter recipient address"
                  required
                  rows={3}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Address *
                </label>
                <Textarea
                  value={transferData.bankAddress}
                  onChange={(e) => setTransferData({...transferData, bankAddress: e.target.value})}
                  placeholder="Enter bank address"
                  required
                  rows={2}
                />
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-2">
                <label className="flex items-center space-x-2 cursor-pointer mb-4">
                  <Checkbox 
                    checked={showIntermediaryBank}
                    onCheckedChange={setShowIntermediaryBank}
                  />
                  <span className="text-sm font-medium text-gray-700">Add Intermediary Bank (Optional)</span>
                </label>
                
                {showIntermediaryBank && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Intermediary Bank Name
                      </label>
                      <Input
                        type="text"
                        value={transferData.intermediaryBankName}
                        onChange={(e) => setTransferData({...transferData, intermediaryBankName: e.target.value})}
                        placeholder="Enter intermediary bank name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Intermediary SWIFT Code
                      </label>
                      <Input
                        type="text"
                        value={transferData.intermediarySwiftCode}
                        onChange={(e) => setTransferData({...transferData, intermediarySwiftCode: e.target.value})}
                        placeholder="Enter SWIFT code"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Intermediary Bank Address
                      </label>
                      <Input
                        type="text"
                        value={transferData.intermediaryBankAddress}
                        onChange={(e) => setTransferData({...transferData, intermediaryBankAddress: e.target.value})}
                        placeholder="Enter bank address"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Amount *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={transferData.amount}
                  onChange={(e) => setTransferData({...transferData, amount: e.target.value})}
                  placeholder="Enter amount"
                  required
                />
              </div>
            </div>
          )}

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg">
            <Send className="w-5 h-5 mr-2" />
            Initiate {wireTransferType === 'domestic' ? 'Domestic' : 'International'} Wire Transfer
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default WireTransfer;
